import type { TalentPoolMatchErrorKind } from "./match-errors";

// The observable states of the Talent Pool comparison tab's content region.
// Discriminated union so the gate (T8) can render each surface exhaustively.
export type MatchTabState =
  | { state: "loading" }
  | { state: "upsell" }
  | { state: "permission_denied" }
  | { state: "invalid_offer" }
  | { state: "rate_limited"; retryAfterSeconds?: number }
  | { state: "semantic_unavailable" }
  | { state: "error" }
  | { state: "empty_pool" }
  | { state: "empty_filtered" }
  | { state: "list" };

// Lifecycle status shared by the capabilities query and the match infinite query.
export type MatchQueryStatus = "pending" | "error" | "success";

export type MatchTabStateInput = {
  capabilitiesStatus: MatchQueryStatus;
  enabled: boolean;
  queryStatus: MatchQueryStatus;
  errorKind?: TalentPoolMatchErrorKind;
  itemCount: number;
  hasActiveFilters: boolean;
};

/**
 * Pure resolver for the pool tab's content state. Consumed by the gate (T8) so the
 * panel (and its match query) mount only past a `list`/empty state.
 *
 * Load-bearing invariants:
 *  - **D16 anti-flash:** while `capabilitiesStatus` is not `success`, the output is
 *    `loading` (pending) or `error` (capabilities failed) — NEVER `upsell`/`empty`.
 *    Rendering upsell/empty before the plan is known would flash the wrong surface.
 *  - **AC8 no-call:** `enabled === false` resolves to `upsell` with no query expected
 *    (the panel gates the data hook on the same `enabled`).
 *  - **AC10 error routing:** a query error routes by `mapTalentPoolError` kind —
 *    `plan_unavailable → upsell` (stale-gating 403 backstop), `not_publisher →
 *    permission_denied` (distinct, no pay CTA), `not_available`(404) → `invalid_offer`,
 *    everything else → retryable `error`.
 */
export const resolveMatchTabState = (
  input: MatchTabStateInput,
): MatchTabState => {
  const {
    capabilitiesStatus,
    enabled,
    queryStatus,
    errorKind,
    itemCount,
    hasActiveFilters,
  } = input;

  // D16: capabilities not yet resolved → only loading (pending) / error.
  if (capabilitiesStatus === "pending") return { state: "loading" };
  if (capabilitiesStatus === "error") return { state: "error" };

  // Capabilities resolved: plan does not include the pool → content-scoped upsell.
  if (!enabled) return { state: "upsell" };

  // Enabled plan: interpret the match query lifecycle.
  if (queryStatus === "pending") return { state: "loading" };

  if (queryStatus === "error") {
    switch (errorKind?.kind) {
      case "plan_unavailable":
        return { state: "upsell" };
      case "not_publisher":
        return { state: "permission_denied" };
      case "not_available":
      case "invalid_offer":
        return { state: "invalid_offer" };
      case "rate_limited":
        return {
          state: "rate_limited",
          retryAfterSeconds: errorKind.retryAfterSeconds,
        };
      case "semantic_unavailable":
        return { state: "semantic_unavailable" };
      default:
        return { state: "error" };
    }
  }

  // queryStatus === "success"
  if (itemCount === 0) {
    return hasActiveFilters
      ? { state: "empty_filtered" }
      : { state: "empty_pool" };
  }
  return { state: "list" };
};
